/**
 * Promo codes API hooks (Wave 2.39 FE, TASK-090).
 *
 * Endpoint contract — `apps/api/src/promo-codes/promo-codes.controller.ts`
 * (shipped on main as Wave 2.38 BE-1):
 *   GET    /admin/promo-codes                  → PromoCodeListResponse
 *   GET    /admin/promo-codes/:id              → PromoCodeCard
 *   GET    /admin/promo-codes/:id/activations  → PromoCodeActivationListResponse
 *   POST   /admin/promo-codes                  → PromoCodeCard
 *   PATCH  /admin/promo-codes/:id              → { id, changes }
 *   POST   /admin/promo-codes/:id/block        → { id, status, blocked_at, block_reason }
 *   POST   /admin/promo-codes/:id/unblock      → { id, status, blocked_at: null }
 *
 * RBAC: BE enforces `@AdminSelectorOrAbove()` on GETs and `@AdminOrAbove()`
 * on writes. FE route guard (`PromosPage`) mirrors the gate so client roles
 * never see the surface. Selector scope (own codes only) is enforced
 * server-side via `internal.selectors.user_id` join.
 *
 * Wire DTOs come straight from `@music-app/shared/schemas/promo-code` —
 * `PromoCodeListResponse`, `PromoCodeCard`, `PromoCodeCreate`,
 * `PromoCodePatch`, `PromoCodeFilter`, `PromoCodeBlock`,
 * `PromoCodeActivationListResponse`. NO local shim (wire-contract iteration
 * 23 — see apps/admin/CLAUDE.md «Wire contract first»).
 *
 * Pagination: keyset (`cursor` / `next_cursor`), `total` is intentionally
 * null. Cursor is invalidated by any filter change.
 *
 * Invalidation strategy (FE M-1 lesson from Wave 2.13 / 2.27): every
 * mutation `await Promise.all([invalidate])` BEFORE resolving so callers
 * can `await mutateAsync(…)` and only then close a modal — by the time
 * the modal unmounts the next list/card fetch is already in flight.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import type {
  PromoCode,
  PromoCodeActivationListResponse,
  PromoCodeBlock,
  PromoCodeCard,
  PromoCodeCreate,
  PromoCodeListResponse,
  PromoCodePatch,
  PromoCodeStatus,
} from '@music-app/shared';
import { apiClient } from '../lib/api-client';
import { STALE_TIME } from '../lib/query-client';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
export const promoCodesListQueryKey = (params: PromoCodesListQueryParams) =>
  ['promo-codes', 'list', params] as const;
export const promoCodeCardQueryKey = (id: string) =>
  ['promo-code-card', id] as const;
export const promoCodeActivationsQueryKey = (
  id: string,
  params: PromoCodeActivationsQueryParams,
) => ['promo-code-activations', id, params] as const;

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------
export interface PromoCodesListFilters {
  q?: string;
  status?: PromoCodeStatus;
  owner_user_id?: string;
}

export interface PromoCodesListQueryParams {
  filters?: PromoCodesListFilters;
  cursor?: string;
  limit?: number;
}

/** Page size used on the wire (`limit=`) and to size the UI footer.
 * Matches `promoCodeFilterSchema.limit.default` in shared. */
export const PROMO_CODES_PAGE_SIZE = 50;
const PROMO_CODES_ENDPOINT = '/admin/promo-codes';

function buildPromoCodesSearchParams(
  params: PromoCodesListQueryParams,
): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.filters?.q) sp.set('q', params.filters.q);
  if (params.filters?.status) sp.set('status', params.filters.status);
  if (params.filters?.owner_user_id) {
    sp.set('owner_user_id', params.filters.owner_user_id);
  }
  sp.set('limit', String(params.limit ?? PROMO_CODES_PAGE_SIZE));
  if (params.cursor) sp.set('cursor', params.cursor);
  return sp;
}

export function usePromoCodesQuery(
  params: PromoCodesListQueryParams,
  options?: { enabled?: boolean },
) {
  return useQuery<PromoCodeListResponse>({
    queryKey: promoCodesListQueryKey(params),
    queryFn: () =>
      apiClient<PromoCodeListResponse>(
        `${PROMO_CODES_ENDPOINT}?${buildPromoCodesSearchParams(params).toString()}`,
      ),
    staleTime: STALE_TIME.list,
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------
export function usePromoCodeQuery(id: string | undefined) {
  return useQuery<PromoCodeCard>({
    queryKey: promoCodeCardQueryKey(id ?? ''),
    queryFn: () => apiClient<PromoCodeCard>(`${PROMO_CODES_ENDPOINT}/${id}`),
    staleTime: STALE_TIME.single,
    enabled: Boolean(id),
  });
}

// ---------------------------------------------------------------------------
// Activations (card tab)
// ---------------------------------------------------------------------------
export interface PromoCodeActivationsQueryParams {
  cursor?: string;
  limit?: number;
}

export const PROMO_CODE_ACTIVATIONS_PAGE_SIZE = 50;

function buildActivationsSearchParams(
  params: PromoCodeActivationsQueryParams,
): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('limit', String(params.limit ?? PROMO_CODE_ACTIVATIONS_PAGE_SIZE));
  if (params.cursor) sp.set('cursor', params.cursor);
  return sp;
}

export function usePromoActivationsQuery(
  id: string | undefined,
  params: PromoCodeActivationsQueryParams,
) {
  return useQuery<PromoCodeActivationListResponse>({
    queryKey: promoCodeActivationsQueryKey(id ?? '', params),
    queryFn: () =>
      apiClient<PromoCodeActivationListResponse>(
        `${PROMO_CODES_ENDPOINT}/${id}/activations?${buildActivationsSearchParams(
          params,
        ).toString()}`,
      ),
    staleTime: STALE_TIME.list,
    enabled: Boolean(id),
    placeholderData: (prev) => prev,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

interface PatchAck {
  id: string;
  changes: Record<string, { from: unknown; to: unknown }>;
}

interface BlockAck {
  id: string;
  status: PromoCodeStatus;
  blocked_at: string;
  block_reason: string;
}

interface UnblockAck {
  id: string;
  status: PromoCodeStatus;
  blocked_at: null;
}

export function useCreatePromoCodeMutation(): UseMutationResult<
  PromoCodeCard,
  Error,
  PromoCodeCreate
> {
  const queryClient = useQueryClient();
  return useMutation<PromoCodeCard, Error, PromoCodeCreate>({
    mutationFn: (input) =>
      apiClient<PromoCodeCard>(PROMO_CODES_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['promo-codes', 'list'] }),
      ]);
    },
  });
}

export function usePatchPromoCodeMutation(
  id: string,
): UseMutationResult<PatchAck, Error, PromoCodePatch> {
  const queryClient = useQueryClient();
  return useMutation<PatchAck, Error, PromoCodePatch>({
    mutationFn: (patch) =>
      apiClient<PatchAck>(`${PROMO_CODES_ENDPOINT}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: promoCodeCardQueryKey(id) }),
        queryClient.invalidateQueries({ queryKey: ['promo-codes', 'list'] }),
      ]);
    },
  });
}

export function useBlockPromoCodeMutation(
  id: string,
): UseMutationResult<BlockAck, Error, PromoCodeBlock> {
  const queryClient = useQueryClient();
  return useMutation<BlockAck, Error, PromoCodeBlock>({
    mutationFn: (body) =>
      apiClient<BlockAck>(`${PROMO_CODES_ENDPOINT}/${id}/block`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: promoCodeCardQueryKey(id) }),
        queryClient.invalidateQueries({ queryKey: ['promo-codes', 'list'] }),
      ]);
    },
  });
}

export function useUnblockPromoCodeMutation(
  id: string,
): UseMutationResult<UnblockAck, Error, void> {
  const queryClient = useQueryClient();
  return useMutation<UnblockAck, Error, void>({
    mutationFn: () =>
      apiClient<UnblockAck>(`${PROMO_CODES_ENDPOINT}/${id}/unblock`, {
        method: 'POST',
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: promoCodeCardQueryKey(id) }),
        queryClient.invalidateQueries({ queryKey: ['promo-codes', 'list'] }),
      ]);
    },
  });
}

// Re-export for ergonomic imports at the call sites.
export type {
  PromoCode,
  PromoCodeCard,
  PromoCodeListResponse,
  PromoCodeActivationListResponse,
};
